import { createContext, ReactNode, use, useRef, useState } from 'react'
import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query'
import {
  type AssistantRunResponse,
  assistantRunOptions,
  assistantThreadTurnsOptions,
  useStartAssistantMessageMutation
} from '~/api/assistant'
import { useMsg } from '~/common/localizationUtils'
import type { ThreadItem } from './types'

export type ChatContextValue = {
  threadItems: ThreadItem[]
  isBusy: boolean
  isThreadLoading: boolean
  isThreadError: boolean
  activeThreadId: string | null
  selectThread: (threadId: string) => void
  reloadThread: () => void
  startNewThread: () => void
  submitMessage: (prompt: string) => Promise<boolean>
}

type Turn = {
  id: string
  question: string
  threadId?: string
  runId?: string
  isError?: boolean
}

const ChatContext = createContext<ChatContextValue | null>(null)

const withRunResult = (
  item: Pick<ThreadItem, 'id' | 'question'>,
  runQuery: UseQueryResult<AssistantRunResponse>,
  errorMessage: string
): ThreadItem => {
  const runResponse = runQuery.data
  if (runResponse?.run.is_final) {
    const answer = runResponse.run.status === 'success' ? runResponse.result?.answer : null
    return answer ? { ...item, answer, citations: runResponse.result?.sources } : { ...item, error: errorMessage }
  }
  if (runQuery.isError) return { ...item, error: errorMessage }
  return { ...item, isPending: true }
}

export const ChatProvider = ({ children }: { children: ReactNode }) => {
  const msg = useMsg()
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null)
  const [turns, setTurns] = useState<Turn[]>([])
  const turnCounter = useRef(0)
  const historyQuery = useQuery(assistantThreadTurnsOptions(selectedThreadId))
  const historyTurns = historyQuery.data?.turns ?? []
  const historyRunQueries = useQueries({
    queries: historyTurns.map(turn => assistantRunOptions(selectedThreadId ?? undefined, turn.pending_run_id))
  })
  const runQueries = useQueries({ queries: turns.map(turn => assistantRunOptions(turn.threadId, turn.runId)) })
  const startMessage = useStartAssistantMessageMutation()

  const activeThreadId = turns.find(turn => turn.threadId)?.threadId ?? selectedThreadId

  const errorMessage = msg('assistant.ai.message.error')
  const historyItems = historyTurns.map((turn, index): ThreadItem => {
    const item = { id: turn.id, question: turn.question }
    return turn.pending_run_id
      ? withRunResult(item, historyRunQueries[index], errorMessage)
      : { ...item, answer: turn.answer ?? undefined, citations: turn.sources }
  })
  const turnItems = turns.map((turn, index): ThreadItem => {
    const item = { id: turn.id, question: turn.question }
    return turn.isError ? { ...item, error: errorMessage } : withRunResult(item, runQueries[index], errorMessage)
  })
  const threadItems = [...historyItems, ...turnItems]
  const isBusy = historyQuery.isFetching || threadItems.some(item => item.isPending)

  const selectThread = (threadId: string) => {
    if (threadId === activeThreadId) {
      if (historyQuery.isError) historyQuery.refetch()
      return
    }
    setSelectedThreadId(threadId)
    setTurns([])
  }

  const startNewThread = () => {
    setSelectedThreadId(null)
    setTurns([])
  }

  const submitMessage = async (prompt: string) => {
    if (isBusy || historyQuery.isError) return false

    turnCounter.current += 1
    const turnId = `turn-${turnCounter.current}`
    const updateTurn = (values: Partial<Turn>) =>
      setTurns(current => current.map(turn => (turn.id === turnId ? { ...turn, ...values } : turn)))

    setTurns(current => [...current, { id: turnId, question: prompt }])
    try {
      const response = await startMessage.mutateAsync({
        content: prompt,
        thread_id: activeThreadId ?? undefined
      })
      updateTurn({ threadId: response.thread.thread_id, runId: response.run_id })
      return true
    } catch {
      updateTurn({ isError: true })
      return false
    }
  }

  return (
    <ChatContext
      value={{
        threadItems,
        isBusy,
        isThreadLoading: historyQuery.isFetching,
        isThreadError: historyQuery.isError,
        activeThreadId,
        selectThread,
        reloadThread: () => historyQuery.refetch(),
        startNewThread,
        submitMessage
      }}>
      {children}
    </ChatContext>
  )
}

export const useChatContext = () => {
  const context = use(ChatContext)
  if (!context) throw new Error('useChatContext must be used within ChatProvider')
  return context
}
